import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { rptService } from '../../../services/rptService';
import ResponsiveDataView from '../../common/ResponsiveDataView';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Collapse,
  TablePagination,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  AttachMoney as AttachMoneyIcon,
  Description as DescriptionIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Home as HomeIcon,
  Download as DownloadIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Map as MapIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import AlertDialog from '../../common/AlertDialog';
import { showNotification } from '../../Notification';
import { CalendarToday as CalendarTodayIcon } from '@mui/icons-material';

const Properties = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preSelectedOwnerId = searchParams.get('owner_id');

  const [properties, setProperties] = useState([]);
  const [totalProperties, setTotalProperties] = useState(0);
  const [owners, setOwners] = useState([]);
  const [propertyKinds, setPropertyKinds] = useState([]);
  const [propertyClassifications, setPropertyClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [barangayFilter, setBarangayFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [allBarangays, setAllBarangays] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [ownerSortDir, setOwnerSortDir] = useState('asc');

  // Debounce search
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Ref for search input
  const searchInputRef = React.useRef(null);
  // Ref for last payment year input
  const lastPaymentYearInputRef = React.useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0); // Reset to first page on filter change
  }, [barangayFilter, statusFilter]);

  useEffect(() => {
    loadProperties();
  }, [page, rowsPerPage, debouncedSearch, barangayFilter, statusFilter, ownerSortDir]);

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssessmentsModalOpen, setIsAssessmentsModalOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [keepModalOpen, setKeepModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [detailsProperty, setDetailsProperty] = useState(null);
  const [payorCount, setPayorCount] = useState(1);
  const [payorNames, setPayorNames] = useState(['']);
  const [remarksText, setRemarksText] = useState('');
  const [isLastYearModalOpen, setIsLastYearModalOpen] = useState(false);
  const [lastYearProperty, setLastYearProperty] = useState(null);
  const [lastPaymentYearInput, setLastPaymentYearInput] = useState(new Date().getFullYear());
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (isLastYearModalOpen) {
      setTimeout(() => {
        lastPaymentYearInputRef.current?.focus();
      }, 100);
    }
  }, [isLastYearModalOpen]);
  

  const ownersById = React.useMemo(() => {
    const m = new Map();
    owners.forEach(o => m.set(o.owner_id, o.owner_name));
    return m;
  }, [owners]);

  const kindsById = React.useMemo(() => {
    const m = new Map();
    propertyKinds.forEach(k => m.set(k.kind_id, k.kind_name));
    return m;
  }, [propertyKinds]);

  const classesById = React.useMemo(() => {
    const m = new Map();
    propertyClassifications.forEach(c => m.set(c.classification_id, c.classification_name));
    return m;
  }, [propertyClassifications]);

  const displayedProperties = React.useMemo(() => {
    const arr = [...properties];
    if (ownerSortDir) {
      arr.sort((a, b) => {
        const an = (a.owner_name || ownersById.get(a.owner_id) || '').toLowerCase();
        const bn = (b.owner_name || ownersById.get(b.owner_id) || '').toLowerCase();
        const cmp = an.localeCompare(bn);
        return ownerSortDir === 'asc' ? cmp : -cmp;
      });
    }
    return arr;
  }, [properties, ownerSortDir, ownersById]);
  
  // Assessment history states
  const [assessments, setAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [isAddAssessmentOpen, setIsAddAssessmentOpen] = useState(false);
  const [assessmentForm, setAssessmentForm] = useState({
    assessed_value: '',
    effectivity_year: new Date().getFullYear(),
    revision_reason: '',
    remarks: ''
  });
  
  // Dialog states
  const [dialogConfig, setDialogConfig] = useState({
    open: false,
    title: '',
    message: '',
    type: 'info', // info, success, warning, error, confirm
    onConfirm: null,
    autoCloseMs: 3000,
    persistent: false
  });
  
  // Form state
  const [formData, setFormData] = useState({
    owner_id: preSelectedOwnerId || '',
    arpn: '',
    td_number: '',
    pin: '',
    lot_number: '',
    block_number: '',
    barangay: '',
    kind_id: '',
    classification_id: '',
    assessed_value: '',
    effectivity_year: new Date().getFullYear(),
    remarks: ''
  });

  // Alphabetically sorted barangays
  const barangays = ['Bakhawan', 'Calabasahan', 'Dalajican', 'Masadya', 'Masudsud', 'Poblacion', 'Sampong', 'San Pedro', 'San Vicente'];
  const uniqueBarangays = Array.from(new Set(properties.map(p => p.barangay).filter(Boolean))).sort();

  useEffect(() => {
    // Initial load handled by the dependency array effect above
    loadOwners();
    loadPropertyKinds();
    loadPropertyClassifications();
    loadAllBarangays();
    
    // If owner_id is in URL, open add modal
    if (preSelectedOwnerId) {
      setFormData(prev => ({ ...prev, owner_id: preSelectedOwnerId }));
      setIsAddModalOpen(true);
    }
  }, [preSelectedOwnerId]);

  const loadProperties = async () => {
    setLoading(true);
    try {
      const result = await rptService.getProperties({
        page,
        pageSize: rowsPerPage,
        search: debouncedSearch,
        barangay: barangayFilter,
        status: statusFilter,
        sortBy: 'owner',
        sortDir: ownerSortDir || undefined
      });
      
      if (result.success) {
        setProperties(result.data);
        setTotalProperties(result.count);
      }
    } catch (error) {
      showAlert('Failed to Load Properties', 'Unable to retrieve property data. Please refresh the page or contact support.', 'error');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadOwners = async () => {
    try {
      const result = await rptService.getAllOwners();
      if (result.success) {
        setOwners(result.data);
      }
    } catch (error) {
      showAlert('Warning', 'Unable to load owner list. You may need to refresh the page.', 'warning');
      console.error('Error loading owners:', error);
    }
  };

  const loadPropertyKinds = async () => {
    try {
      const result = await rptService.getPropertyKinds();
      if (result.success) {
        setPropertyKinds(result.data);
      }
    } catch (error) {
      console.error('Error loading property kinds:', error);
    }
  };

  const loadPropertyClassifications = async () => {
    try {
      const result = await rptService.getPropertyClassifications();
      if (result.success) {
        setPropertyClassifications(result.data);
      }
    } catch (error) {
      console.error('Error loading property classifications:', error);
    }
  };

  const loadAllBarangays = async () => {
    try {
      const result = await rptService.getAllBarangays();
      if (result.success) {
        setAllBarangays(result.data);
      }
    } catch (error) {
      console.error('Error loading barangays:', error);
    }
  };

  const showAlert = (title, message, type = 'info', options = {}) => {
    const duration = typeof options.autoCloseMs === 'number' ? options.autoCloseMs : undefined;
    showNotification(`${title}\n${message}`, type, duration);
  };

  const showConfirm = (title, message, onConfirm) => {
    setDialogConfig({
      open: true,
      title,
      message,
      type: 'confirm',
      onConfirm,
      autoCloseMs: 0,
      persistent: true
    });
  };

  const closeDialog = () => {
    setDialogConfig({ ...dialogConfig, open: false });
  };

  const parsePayorsFromRemarks = (remarks) => {
    if (!remarks) return { count: 1, names: [], extra: '' };
    const lines = String(remarks).split('\n');
    const first = lines[0] || '';
    if (first.startsWith('PAYORS_JSON:')) {
      try {
        const payload = JSON.parse(first.replace('PAYORS_JSON:', ''));
        const extra = lines.slice(1).join('\n');
        return { count: payload.count || 1, names: payload.names || [], extra };
      } catch (e) {
        return { count: 1, names: [], extra: remarks };
      }
    }
    return { count: 1, names: [], extra: remarks };
  };

  const serializePayorsToRemarks = (count, names, extra) => {
    const payload = { count: Math.max(1, parseInt(count) || 1), names: (names || []).filter(n => n && n.trim()) };
    const header = 'PAYORS_JSON:' + JSON.stringify(payload);
    return extra ? `${header}\n${extra}` : header;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddProperty = async () => {
    const errs = {};
    if (!String(formData.owner_id || '').trim()) errs.owner_id = 'Property Owner is required';
    if (!String(formData.td_number || '').trim()) errs.td_number = 'Tax Declaration No. is required';
    if (!String(formData.pin || '').trim()) errs.pin = 'PIN is required';
    if (!String(formData.assessed_value || '').trim()) errs.assessed_value = 'Assessed Value is required';
    if (!String(formData.effectivity_year || '').trim()) errs.effectivity_year = 'Effectivity Year is required';
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      showAlert('Required Fields Missing', 'Please complete all required fields marked with *.', 'warning');
      return;
    }

    try {
      // Separate property data from assessment data
      const { assessed_value, effectivity_year, ...propertyData } = formData;
      propertyData.remarks = remarksText;
      
      // First, add the property
      const propertyResult = await rptService.addProperty(propertyData);
      if (!propertyResult.success) {
        showAlert('Error Adding Property', propertyResult.error, 'error');
        return;
      }

      // Then, create the initial assessment
      const assessmentResult = await rptService.addPropertyAssessment({
        property_id: propertyResult.data.property_id,
        assessed_value: assessed_value,
        effectivity_year: effectivity_year,
        revision_reason: 'Initial Assessment',
        remarks: 'Initial property assessment'
      });

      if (assessmentResult.success) {
        const ownerName = getOwnerName(formData.owner_id);
        showAlert(
          'Property Added Successfully!',
          `TD Number: ${formData.td_number}\nPIN: ${formData.pin}\nOwner: ${ownerName}\nAssessed Value: ₱${parseFloat(assessed_value).toLocaleString()}`,
          'success'
        );
        resetForm();
        loadProperties();
        
        if (!keepModalOpen) {
          setIsAddModalOpen(false);
          // Clear URL params
          navigate('/portal/property-tax/properties', { replace: true });
        }
      } else {
        // Property was created but assessment failed - warn user
        showAlert(
          'Property Created, Assessment Failed',
          'Property was created successfully but the initial assessment could not be saved. Please add it manually using the Assessments button.',
          'warning'
        );
        loadProperties();
        if (!keepModalOpen) {
          setIsAddModalOpen(false);
        }
      }
    } catch (error) {
      showAlert('System Error', 'Unable to add property. Please try again or contact support.', 'error');
      console.error(error);
    }
  };

  const handleEditProperty = async () => {
    if (!formData.owner_id || !formData.td_number || !formData.pin) {
      showAlert('Required Fields Missing', 'Please fill in the following:\n\n• Property Owner\n• Tax Declaration Number\n• PIN', 'warning');
      return;
    }

    try {
      const propertyData = { ...formData };
      const parsed = parsePayorsFromRemarks(selectedProperty?.remarks || '');
      propertyData.remarks = serializePayorsToRemarks(parsed.count, parsed.names, remarksText);
      const result = await rptService.updateProperty(selectedProperty.property_id, propertyData);
      if (result.success) {
        showAlert(
          'Property Updated',
          `TD Number: ${formData.td_number}\n\nChanges have been saved successfully.`,
          'success'
        );
        setIsEditModalOpen(false);
        resetForm();
        setSelectedProperty(null);
        loadProperties();
      } else {
        showAlert('Error Updating Property', result.error, 'error');
      }
    } catch (error) {
      showAlert('System Error', 'Unable to update property. Please try again or contact support.', 'error');
      console.error(error);
    }
  };

  const handleDeleteProperty = async (property) => {
    const ownerName = property.owner_name || getOwnerName(property.owner_id);
    const confirmMessage = `Are you sure you want to delete this property?\n\nTD Number: ${property.td_number}\nPIN: ${property.pin || 'N/A'}\nOwner: ${ownerName}${property.barangay ? '\nBarangay: ' + property.barangay : ''}\n\n⚠️ This action cannot be undone!`;
    
    showConfirm(
      'Delete Confirmation',
      confirmMessage,
      async () => {
        closeDialog();
        try {
          const result = await rptService.deleteProperty(property.property_id);
          if (result.success) {
            showAlert(
              'Property Deleted',
              `TD Number: ${property.td_number}\n\nProperty has been removed from the registry.`,
              'success'
            );
            loadProperties();
          } else {
            showAlert('Error Deleting Property', result.error, 'error');
          }
        } catch (error) {
          showAlert('System Error', 'Unable to delete property. Please try again or contact support.', 'error');
          console.error(error);
        }
      }
    );
  };

  const openEditModal = (property) => {
    setSelectedProperty(property);
    setFormData({
      owner_id: property.owner_id || '',
      arpn: property.arpn || '',
      td_number: property.td_number || '',
      pin: property.pin || '',
      lot_number: property.lot_number || '',
      block_number: property.block_number || '',
      barangay: property.barangay || '',
      kind_id: property.kind_id || '',
      classification_id: property.classification_id || '',
      remarks: property.remarks || ''
    });
    const parsed = parsePayorsFromRemarks(property.remarks || '');
    setRemarksText(parsed.extra || '');
    setIsEditModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      owner_id: preSelectedOwnerId || '',
      arpn: '',
      td_number: '',
      pin: '',
      lot_number: '',
      block_number: '',
      barangay: '',
      kind_id: '',
      classification_id: '',
      remarks: '',
      assessed_value: '',
      effectivity_year: new Date().getFullYear()
    });
    setPayorCount(1);
    setPayorNames(['']);
    setRemarksText('');
  };

  // Assessment Management Functions
  const handleViewAssessments = async (property) => {
    setSelectedProperty(property);
    setIsAssessmentsModalOpen(true);
    setLoadingAssessments(true);
    
    try {
      const result = await rptService.getPropertyAssessments(property.property_id);
      if (result.success) {
        setAssessments(result.data);
      } else {
        showAlert('Error Loading Assessments', result.error, 'error');
        setAssessments([]);
      }
    } catch (error) {
      showAlert('Failed to Load Assessments', 'Unable to retrieve assessment history.', 'error');
      console.error(error);
      setAssessments([]);
    } finally {
      setLoadingAssessments(false);
    }
  };

  const handleAddAssessment = async () => {
    if (!assessmentForm.assessed_value || !assessmentForm.effectivity_year) {
      showAlert('Required Fields Missing', 'Please fill in:\n\n• Assessed Value\n• Effectivity Year', 'warning');
      return;
    }

    try {
      const result = await rptService.addPropertyAssessment({
        property_id: selectedProperty.property_id,
        ...assessmentForm
      });
      
      if (result.success) {
        showAlert('Assessment Added!', `Assessed Value: ₱${parseFloat(assessmentForm.assessed_value).toLocaleString()}\nEffective: ${assessmentForm.effectivity_year}`, 'success');
        setAssessmentForm({
          assessed_value: '',
          effectivity_year: new Date().getFullYear(),
          revision_reason: '',
          remarks: ''
        });
        setIsAddAssessmentOpen(false);
        // Reload assessments
        handleViewAssessments(selectedProperty);
        loadProperties(); // Refresh properties to show updated current value
      } else {
        showAlert('Error Adding Assessment', result.error, 'error');
      }
    } catch (error) {
      showAlert('System Error', 'Unable to add assessment. Please try again.', 'error');
      console.error(error);
    }
  };

  const handleDeleteAssessment = async (assessment) => {
    showConfirm(
      'Delete Assessment',
      `Are you sure you want to delete this assessment?\n\nAssessed Value: ₱${parseFloat(assessment.assessed_value).toLocaleString()}\nEffective Year: ${assessment.effectivity_year}\n\n⚠️ This action cannot be undone!`,
      async () => {
        closeDialog();
        try {
          const result = await rptService.deletePropertyAssessment(assessment.assessment_id);
          if (result.success) {
            showAlert('Assessment Deleted', 'Assessment has been removed from history.', 'success');
            handleViewAssessments(selectedProperty); // Reload
            loadProperties();
          } else {
            showAlert('Error Deleting Assessment', result.error, 'error');
          }
        } catch (error) {
          showAlert('System Error', 'Unable to delete assessment.', 'error');
          console.error(error);
        }
      }
    );
  };

  // No client-side filtering needed anymore
  // const filteredProperties = ...

  // No client-side pagination needed anymore
  // const paginatedProperties = ...

  function getOwnerName(ownerId) {
    return ownersById.get(ownerId) || 'Unknown';
  }

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    if (Number.isNaN(num)) return '₱0.00';
    return `₱${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (value) => {
    if (!value) return 'N/A';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  };

  const extractPaidBy = (pay) => {
    const remark = String(pay?.remarks || '');
    const m = remark.match(/Paid by:\s*([^\n]+)/i);
    if (m && m[1]) return m[1].trim();
    if (pay?.payor_name) return String(pay.payor_name).trim();
    if (pay?.processed_by) return String(pay.processed_by).trim();
    return 'N/A';
  };

  const getPaymentStatus = (lastPaymentYear) => {
    const currentYear = new Date().getFullYear();
    if (!lastPaymentYear) return 'No Payment';
    if (lastPaymentYear < currentYear - 1) return 'Delinquent';
    if (lastPaymentYear === currentYear - 1) return 'For Payment';
    if (lastPaymentYear === currentYear) return 'Updated';
    if (lastPaymentYear > currentYear) return 'Advance';
    return 'No Payment';
  };

  const openPropertyDetails = (property) => {
    setDetailsProperty(property);
    const parsed = parsePayorsFromRemarks(property.remarks || '');
    setPayorCount(parsed.count);
    setPayorNames(parsed.names.length ? parsed.names : Array.from({ length: parsed.count }, () => ''));
    setRemarksText(parsed.extra || '');
    setIsDetailsModalOpen(true);
  };

  const closePropertyDetails = () => {
    setIsDetailsModalOpen(false);
    setDetailsProperty(null);
  };

  const handlePayProperty = (property) => {
    navigate(`/portal/property-tax/payments?property_id=${property.property_id}`);
  };

  const openLastPaymentYearModal = (property) => {
    setLastYearProperty(property);
    const current = new Date().getFullYear();
    const seedYear = property.last_payment_year || current;
    setLastPaymentYearInput(seedYear);
    setIsLastYearModalOpen(true);
  };

  const closeLastPaymentYearModal = () => {
    setIsLastYearModalOpen(false);
    setLastYearProperty(null);
  };

  const saveLastPaymentYear = async () => {
    if (!lastYearProperty) return;
    const y = parseInt(lastPaymentYearInput);
    if (!y || y < 1900 || y > new Date().getFullYear() + 10) {
      showAlert('Invalid Year', 'Please enter a valid year.', 'warning');
      return;
    }
    const res = await rptService.recordLastPaymentYear(lastYearProperty.property_id, lastYearProperty.owner_id, y);
    if (res && res.success) {
      closeLastPaymentYearModal();
      await loadProperties();
      showAlert('Last Payment Year Saved', `TD: ${lastYearProperty.td_number}\nMarked paid until: ${y}`, 'success');
      // Focus search box after saving
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      showAlert('Save Failed', res?.error || 'Unable to record year-only payment.', 'error');
    }
  };

  const handleExport = async () => {
    setLoading(true);
    try {
      const allData = [];
      const batchSize = 50; // Use smaller batch size to avoid URL length issues with IN clauses in rptService
      const totalPages = Math.ceil(totalProperties / batchSize);
      
      // Show loading notification if it might take a while
      if (totalPages > 2) {
        showAlert('Exporting...', `Exporting ${totalProperties} records. Please wait...`, 'info', { autoCloseMs: 2000 });
      }

      for (let p = 0; p < totalPages; p++) {
        const result = await rptService.getProperties({
          page: p,
          pageSize: batchSize,
          search: debouncedSearch,
          barangay: barangayFilter,
          sortBy: 'owner',
          sortDir: ownerSortDir || undefined
        });
        
        if (result.success) {
          allData.push(...result.data);
        }
      }

      if (allData.length === 0) {
        showAlert('Export Failed', 'No records found to export.', 'warning');
        return;
      }

      const csvHeader = [
        'TD Number',
        'PIN',
        'Owner',
        'Address',
        'Barangay',
        'Kind',
        'Classification',
        'Assessed Value',
        'Effectivity Year',
        'Last Payment Year',
        'Remarks'
      ].join(',');

      const csvRows = allData.map(prop => {
        return [
          `"${prop.td_number || ''}"`,
          `"${prop.pin || ''}"`,
          `"${prop.owner_name || ''}"`,
          `"${prop.owner_address || ''}"`,
          `"${prop.barangay || ''}"`,
          `"${prop.kind_name || ''}"`,
          `"${prop.classification_name || ''}"`,
          prop.assessed_value ? parseFloat(prop.assessed_value).toFixed(2) : '0.00',
          prop.effectivity_year || '',
          prop.last_payment_year || '',
          `"${(prop.remarks || '').replace(/"/g, '""')}"`
        ].join(',');
      });

      const csvContent = [csvHeader, ...csvRows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `properties_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showAlert('Export Complete', `Successfully exported ${allData.length} records.`, 'success');

    } catch (error) {
      console.error('Export error:', error);
      showAlert('Export Failed', 'An error occurred while exporting data.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPropertyReports = async () => {
    setLoading(true);
    try {
      const allData = [];
      const batchSize = 50;
      const totalPages = Math.ceil(totalProperties / batchSize);

      if (totalPages === 0) {
        showAlert('No Records', 'No properties found to print.', 'warning');
        return;
      }

      for (let p = 0; p < totalPages; p++) {
        const result = await rptService.getProperties({
          page: p,
          pageSize: batchSize,
          search: debouncedSearch,
          barangay: barangayFilter,
          status: statusFilter,
          sortBy: 'owner',
          sortDir: ownerSortDir || undefined
        });

        if (!result.success) {
          throw new Error(result.error || 'Unable to load properties for printing.');
        }
        allData.push(...(result.data || []));
      }

      if (allData.length === 0) {
        showAlert('No Records', 'No properties found to print.', 'warning');
        return;
      }

      const propertyIds = allData.map(p => p.property_id).filter(Boolean);
      const chunkSize = 25;
      const allPayments = [];

      for (let i = 0; i < propertyIds.length; i += chunkSize) {
        const chunk = propertyIds.slice(i, i + chunkSize);
        const res = await rptService.getPaymentsForProperties(chunk);
        if (!res.success) {
          throw new Error(res.error || 'Unable to load payment history for printing.');
        }
        allPayments.push(...(res.data || []));
      }

      const paymentsByProperty = allPayments.reduce((acc, p) => {
        const key = p.property_id;
        if (!key) return acc;
        if (!acc[key]) acc[key] = [];
        acc[key].push(p);
        return acc;
      }, {});

      Object.keys(paymentsByProperty).forEach((key) => {
        paymentsByProperty[key].sort((a, b) => new Date(a.payment_date) - new Date(b.payment_date));
      });

      // Sort properties alphabetically by owner name
      allData.sort((a, b) => {
        const ownerNameA = (a.owner_name || ownersById.get(a.owner_id) || '').toLowerCase();
        const ownerNameB = (b.owner_name || ownersById.get(b.owner_id) || '').toLowerCase();
        return ownerNameA.localeCompare(ownerNameB);
      });

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        showAlert('Popup Blocked', 'Please allow popups to print property reports.', 'warning');
        return;
      }

      const printedOn = new Date().toLocaleString('en-US', { year: 'numeric', month: 'long', day: '2-digit', hour: '2-digit', minute: '2-digit' });
      const filterSummary = [
        debouncedSearch ? `Search: ${debouncedSearch}` : null,
        barangayFilter ? `Barangay: ${barangayFilter}` : null,
        statusFilter ? `Status: ${statusFilter}` : null
      ].filter(Boolean).join(' • ') || 'All Properties';

      const pagesHtml = allData.map((prop) => {
        const ownerName = prop.owner_name || getOwnerName(prop.owner_id);
        const assessed = prop.assessed_value ? formatCurrency(prop.assessed_value) : 'N/A';
        const payorsParsed = parsePayorsFromRemarks(prop.remarks || '');
        const payorNames = (payorsParsed?.names || []).filter(n => n && String(n).trim());
        const payorsText = payorNames.length > 0 ? payorNames.join(', ') : '';
        const totalRows = 18;
        const payments = (paymentsByProperty[prop.property_id] || []).slice(0, totalRows);

        const paymentRows = Array.from({ length: totalRows }).map((_, idx) => {
          const pay = payments[idx];
          if (!pay) {
            return `
              <tr class="blank-row">
                <td>&nbsp;</td>
                <td class="num">&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
                <td>&nbsp;</td>
              </tr>
            `;
          }

          const years = pay.year_from && pay.year_to ? `${pay.year_from} - ${pay.year_to}` : 'N/A';
          const paidBy = extractPaidBy(pay);
          return `
            <tr>
              <td>${escapeHtml(years)}</td>
              <td class="num">${escapeHtml(formatCurrency(pay.amount_paid))}</td>
              <td>${escapeHtml(pay.or_number || 'N/A')}</td>
              <td>${escapeHtml(paidBy)}</td>
              <td>${escapeHtml(formatDate(pay.payment_date))}</td>
            </tr>
          `;
        }).join('');

        return `
          <section class="page">
            <div class="card details-card">
              <div class="card-head">
                <div class="doc-title">${escapeHtml(ownerName)}</div>
                <div class="doc-subtitle">PIN: ${escapeHtml(prop.pin || 'N/A')} • Barangay: ${escapeHtml(prop.barangay || 'N/A')}</div>
              </div>
              <div class="card-title">Property Details</div>
              <div class="details-grid">
                <div class="detail">
                  <div class="label">TD #</div>
                  <div class="value">${escapeHtml(prop.td_number || 'N/A')}</div>
                </div>
                <div class="detail">
                  <div class="label">Kind / Classification</div>
                  <div class="value">${escapeHtml(`${prop.kind_name || 'N/A'}${prop.classification_name ? ` / ${prop.classification_name}` : ''}`)}</div>
                </div>
                <div class="detail">
                  <div class="label">Assessed Value</div>
                  <div class="value value-strong">${escapeHtml(assessed)}</div>
                </div>
                <div class="detail">
                  <div class="label">Payors</div>
                  <div class="value">${escapeHtml(payorsText)}</div>
                </div>
              </div>
            </div>

            <div class="card history-card">
              <div class="card-title">Payment History</div>
              <table class="history">
                <thead>
                  <tr>
                    <th>Year Covered</th>
                    <th class="num">Amount</th>
                    <th>OR Number</th>
                    <th>Paid By</th>
                    <th>Date Paid</th>
                  </tr>
                </thead>
                <tbody>
                  ${paymentRows}
                </tbody>
              </table>
            </div>
          </section>
        `;
      }).join('');

      const html = `
        <!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>Property Reports</title>
            <style>
              @page { size: A4; margin: 12mm; }
              * { box-sizing: border-box; }
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #0f172a; background: #ffffff; }
              .page { page-break-after: always; }
              .doc-title { font-size: 20px; font-weight: 950; letter-spacing: -0.02em; }
              .doc-subtitle { margin-top: 4px; font-size: 11px; color: #334155; font-weight: 600; }
              .card { margin-top: 12px; padding: 12px 12px 10px; border-radius: 12px; border: 1.5px solid #0f172a; background: #ffffff; }
              .card-title { font-size: 12px; font-weight: 950; color: #0f172a; margin-bottom: 10px; padding: 6px 10px; border-radius: 10px; background: #f1f5f9; border: 0; }
              .details-card { border-color: #0f172a; background: #ffffff; }
              .card-head { margin-bottom: 8px; padding-bottom: 8px; border-bottom: 1px solid #94a3b8; }
              .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px 18px; }
              .detail .label { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: 0.06em; }
              .detail .value { margin-top: 2px; font-size: 12px; color: #0f172a; font-weight: 600; }
              .detail .value-strong { font-size: 13px; font-weight: 900; }
              table { width: 100%; border-collapse: collapse; }
              .history { border: 2px solid #0f172a; }
              .history thead th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: #0f172a; text-align: center; padding: 10px 6px; background: #ffffff; border: 1px solid #334155; }
              .history tbody td { font-size: 11px; padding: 10px 6px; border: 1px solid #334155; vertical-align: middle; text-align: center; }
              .history tbody tr.blank-row td { padding: 14px 6px; }
              .num { text-align: center; }
              .muted { color: #64748b; }
              @media print {
                body { background: #ffffff; }
                .card { box-shadow: none; }
              }
            </style>
          </head>
          <body>
            ${pagesHtml}
            <script>
              window.onload = function() { setTimeout(function() { window.print(); }, 50); };
            </script>
          </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (error) {
      console.error('Print property reports error:', error);
      showAlert('Print Failed', error?.message || 'An error occurred while preparing the report.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderPropertyForm = (isEdit = false) => (
    <Grid container spacing={2}>
      {/* Property Owner */}
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel>Property Owner *</InputLabel>
          <Select
            value={formData.owner_id}
            label="Property Owner *"
            onChange={(e) => setFormData({...formData, owner_id: e.target.value})}
          >
            <MenuItem value="">Select Owner</MenuItem>
            {owners.map(owner => (
              <MenuItem key={owner.owner_id} value={owner.owner_id}>{owner.owner_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* ARPN (Optional) */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="ARPN"
          placeholder="ARP Number (optional)"
          value={formData.arpn}
          onChange={(e) => setFormData({...formData, arpn: e.target.value})}
        />
      </Grid>

      {/* Tax Declaration No. */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Tax Declaration No. *"
          placeholder="TD Number"
          value={formData.td_number}
          onChange={(e) => { setFormData({...formData, td_number: e.target.value}); setFormErrors(prev => ({ ...prev, td_number: e.target.value ? undefined : (prev.td_number) })); }}
          error={Boolean(formErrors.td_number)}
          helperText={formErrors.td_number || ''}
        />
      </Grid>

      {/* PIN */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="PIN *"
          placeholder="Property Identification Number"
          value={formData.pin}
          onChange={(e) => { setFormData({...formData, pin: e.target.value}); setFormErrors(prev => ({ ...prev, pin: e.target.value ? undefined : (prev.pin) })); }}
          error={Boolean(formErrors.pin)}
          helperText={formErrors.pin || ''}
        />
      </Grid>

      {/* Lot/Block No. */}
      <Grid item xs={6} sm={3}>
        <TextField
          fullWidth
          label="Lot No."
          value={formData.lot_number}
          onChange={(e) => setFormData({...formData, lot_number: e.target.value})}
        />
      </Grid>
      <Grid item xs={6} sm={3}>
        <TextField
          fullWidth
          label="Block No."
          value={formData.block_number}
          onChange={(e) => setFormData({...formData, block_number: e.target.value})}
        />
      </Grid>

      {/* Barangay */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Barangay</InputLabel>
          <Select
            value={formData.barangay}
            label="Barangay"
            onChange={(e) => setFormData({...formData, barangay: e.target.value})}
          >
            <MenuItem value="">Select Barangay</MenuItem>
            {barangays.map(brgy => <MenuItem key={brgy} value={brgy}>{brgy}</MenuItem>)}
          </Select>
        </FormControl>
      </Grid>

      {/* Kind */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Kind</InputLabel>
          <Select
            value={formData.kind_id}
            label="Kind"
            onChange={(e) => setFormData({...formData, kind_id: e.target.value})}
          >
            <MenuItem value="">Select Kind</MenuItem>
            {propertyKinds.map(kind => (
              <MenuItem key={kind.kind_id} value={kind.kind_id}>{kind.kind_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Classification */}
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth>
          <InputLabel>Classification</InputLabel>
          <Select
            value={formData.classification_id}
            label="Classification"
            onChange={(e) => setFormData({...formData, classification_id: e.target.value})}
          >
            <MenuItem value="">Select Classification</MenuItem>
            {propertyClassifications.map(cls => (
              <MenuItem key={cls.classification_id} value={cls.classification_id}>{cls.classification_name}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>

      {/* Initial Assessment - Only for Add Mode */}
      {!isEdit && (
        <Grid item xs={12}>
          <Alert severity="info" icon={<AttachMoneyIcon />}>
            <strong>Initial Assessment:</strong> Provide the initial assessed value. Additional assessments can be added later via the "Assessments" button.
          </Alert>
        </Grid>
      )}

      {/* Assessed Value */}
      {!isEdit && (
        <>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Assessed Value *"
              type="number"
              inputProps={{ step: "0.01" }}
              placeholder="0.00"
              value={formData.assessed_value}
              onChange={(e) => { setFormData({...formData, assessed_value: e.target.value}); setFormErrors(prev => ({ ...prev, assessed_value: e.target.value ? undefined : (prev.assessed_value) })); }}
              error={Boolean(formErrors.assessed_value)}
              helperText={formErrors.assessed_value || 'Initial assessed property value'}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Effectivity Year *"
              type="number"
              placeholder="Year"
              value={formData.effectivity_year}
              onChange={(e) => { setFormData({...formData, effectivity_year: e.target.value}); setFormErrors(prev => ({ ...prev, effectivity_year: e.target.value ? undefined : (prev.effectivity_year) })); }}
              error={Boolean(formErrors.effectivity_year)}
              helperText={formErrors.effectivity_year || 'Year this assessment becomes effective'}
            />
          </Grid>
        </>
      )}

      {/* Remarks */}
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Remarks"
          multiline
          rows={3}
          placeholder="Additional remarks or notes"
          value={remarksText}
          onChange={(e) => setRemarksText(e.target.value)}
        />
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ flexGrow: 1, pb: 4, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ pt: 3, pb: 8 }}>
        {/* Breadcrumbs */}
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: '0.875rem' }}>
            <Link
              underline="hover"
              color="inherit"
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
              onClick={() => navigate('/portal/dashboard')}
            >
              <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
              Portal
            </Link>
            <Link
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={() => navigate('/portal/property-tax/dashboard')}
            >
              RPT System
            </Link>
            <Typography color="text.primary" sx={{ fontWeight: 500 }}>Properties</Typography>
          </Breadcrumbs>
        </Box>

        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ 
              fontWeight: 800, 
              color: '#1e293b', 
              letterSpacing: '-0.02em',
              background: 'linear-gradient(45deg, #1e293b 30%, #334155 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              <BusinessIcon fontSize="large" sx={{ color: '#3b82f6' }} /> Property Registry
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '800px', lineHeight: 1.6 }}>
              Manage real property records, assessments, and ownership information.
            </Typography>
          </Box>
          <Button 
            variant="contained" 
            size="large"
            startIcon={<AddIcon />} 
            onClick={() => { resetForm(); setIsAddModalOpen(true); }}
            sx={{ 
              bgcolor: '#1e293b', 
              '&:hover': { bgcolor: '#334155' },
              px: 3,
              py: 1.5,
              borderRadius: 1.5,
              fontWeight: 600,
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)'
            }}
          >
            Add New Property
          </Button>
        </Box>

        {/* Search & Filter */}
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2, border: '1px solid #e2e8f0', boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                inputRef={searchInputRef}
                placeholder="Search by TD number, PIN, ARPN, owner, or barangay..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { bgcolor: '#f8fafc' }
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: '#e2e8f0' },
                    '&:hover fieldset': { borderColor: '#cbd5e1' },
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel shrink>Barangay</InputLabel>
                <Select
                  value={barangayFilter}
                  label="Barangay"
                  onChange={(e) => setBarangayFilter(e.target.value)}
                  displayEmpty
                  sx={{ 
                    bgcolor: '#f8fafc',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value="">
                    <em>All Barangays</em>
                  </MenuItem>
                  {(allBarangays.length ? allBarangays : (uniqueBarangays.length ? uniqueBarangays : barangays)).map(brgy => (
                    <MenuItem key={brgy} value={brgy}>{brgy}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel shrink>Payment Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Payment Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  sx={{ 
                    bgcolor: '#f8fafc',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value="">
                    <em>All Status</em>
                  </MenuItem>
                  <MenuItem value="updated">Updated</MenuItem>
                  <MenuItem value="due">For Payment</MenuItem>
                  <MenuItem value="delinquent">Delinquent</MenuItem>
                  <MenuItem value="advance">Advance</MenuItem>
                  <MenuItem value="no_payment">No Payment</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={loading || properties.length === 0}
                fullWidth
                sx={{ height: 56, borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
              >
                Export
              </Button>
            </Grid>
          </Grid>
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintPropertyReports}
              disabled={loading || totalProperties === 0}
              sx={{ borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' } }}
            >
              Print Property Reports
            </Button>
          </Box>
        </Paper>

        {/* Properties Responsive Data View */}
        <ResponsiveDataView
          columns={[
            {
              key: 'td_number',
              label: 'TD Number / PIN',
              priority: 'high',
              render: (property) => (
                <Box>
                  <Typography variant="subtitle2" fontWeight="600" color="#334155">{property.td_number}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">PIN: {property.pin || 'N/A'}</Typography>
                  {property.arpn && <Typography variant="caption" display="block" color="text.secondary">ARPN: {property.arpn}</Typography>}
                </Box>
              )
            },
            {
              key: 'owner',
              label: 'Owner',
              priority: 'high',
              render: (property) => (
                <Box>
                  <Typography variant="body2" fontWeight="500" color="#1e293b">{property.owner_name || getOwnerName(property.owner_id)}</Typography>
                  <Typography variant="caption" color="text.secondary">Eff. Year: {property.effectivity_year}</Typography>
                </Box>
              )
            },
            {
              key: 'barangay',
              label: 'Barangay',
              priority: 'medium',
              render: (property) => (
                <Box>
                  <Typography variant="body2" color="#475569">{property.barangay || 'N/A'}</Typography>
                  {property.lot_number && (
                    <Typography variant="caption" color="text.secondary">
                      Lot {property.lot_number}{property.block_number && `, Blk ${property.block_number}`}
                    </Typography>
                  )}
                </Box>
              )
            },
            {
              key: 'kind',
              label: 'Kind / Class',
              priority: 'medium',
              render: (property) => {
                const kindLabel = kindsById.get(property.kind_id);
                const classificationLabel = classesById.get(property.classification_id);
                return (
                  <Stack direction="row" spacing={0.5}>
                    {kindLabel && <Chip label={kindLabel} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: '#e0f2fe', color: '#0369a1' }} />}
                    {classificationLabel && <Chip label={classificationLabel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem', borderColor: '#cbd5e1', color: '#64748b' }} />}
                    {!kindLabel && !classificationLabel && <Typography variant="caption" color="text.secondary">N/A</Typography>}
                  </Stack>
                );
              }
            },
            {
              key: 'status',
              label: 'Status',
              priority: 'high',
              render: (property) => {
                const currentYear = new Date().getFullYear();
                const lastPaymentYear = property.last_payment_year;
                let statusLabel = 'No Payment';
                let statusColor = 'default';
                if (lastPaymentYear) {
                  if (lastPaymentYear < currentYear - 1) {
                    statusLabel = 'Delinquent';
                    statusColor = 'error';
                  } else if (lastPaymentYear === currentYear - 1) {
                    statusLabel = 'For Payment';
                    statusColor = 'warning';
                  } else if (lastPaymentYear === currentYear) {
                    statusLabel = 'Updated';
                    statusColor = 'success';
                  } else if (lastPaymentYear > currentYear) {
                    statusLabel = 'Advance';
                    statusColor = 'info';
                  }
                }
                return (
                  <Box>
                    <Chip 
                      label={statusLabel} 
                      color={statusColor} 
                      size="small" 
                      variant={statusLabel === 'No Payment' ? 'outlined' : 'filled'}
                      sx={{ fontWeight: 'bold', height: 24 }}
                    />
                    {lastPaymentYear && (
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                        Until: {lastPaymentYear}
                      </Typography>
                    )}
                  </Box>
                );
              }
            },
            {
              key: 'assessed_value',
              label: 'Assessed Value',
              priority: 'high',
              align: 'right',
              render: (property) => (
                <Typography variant="body2" fontWeight="600" color="#0f172a">
                  {property.assessed_value ? `₱${parseFloat(property.assessed_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                </Typography>
              )
            },
            {
              key: 'actions',
              label: 'Actions',
              priority: 'high',
              align: 'center',
              render: (property) => (
                <Stack direction="row" spacing={0.5} justifyContent="center">
                  <Tooltip title="Manage Assessed Values">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#d97706', '&:hover': { bgcolor: '#fef3c7' } }}
                      onClick={(e) => { e.stopPropagation(); handleViewAssessments(property); }}
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Property">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#3b82f6', '&:hover': { bgcolor: '#eff6ff' } }}
                      onClick={(e) => { e.stopPropagation(); openEditModal(property); }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Pay">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#22c55e', '&:hover': { bgcolor: '#f0fdf4' } }}
                      onClick={(e) => { e.stopPropagation(); handlePayProperty(property); }}
                    >
                      <AttachMoneyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Set Last Payment Year">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#6366f1', '&:hover': { bgcolor: '#e0e7ff' } }}
                      onClick={(e) => { e.stopPropagation(); openLastPaymentYearModal(property); }}
                    >
                      <CalendarTodayIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Property">
                    <IconButton 
                      size="small" 
                      sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                      onClick={(e) => { e.stopPropagation(); handleDeleteProperty(property); }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              )
            }
          ]}
          data={displayedProperties}
          loading={loading}
          loadingMessage="Loading real property tax records..."
          emptyMessage={searchTerm ? 'Try adjusting your search criteria' : 'Add your first property to get started'}
          rowKey="property_id"
          onRowClick={(prop) => openPropertyDetails(prop)}
          paginationSlot={
            <TablePagination
              rowsPerPageOptions={[20, 50, 100]}
              component="div"
              count={totalProperties}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          }
        />

        {/* Footer */}
        <Box component="footer" sx={{ py: 6, textAlign: 'center', borderTop: '1px solid #e2e8f0', mt: 8 }}>
          <Typography variant="body2" color="text.secondary">
            © {new Date().getFullYear()} Real Property Tax System • Official Government Portal
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ mt: 1, display: 'block' }}>
            Authorized Personnel Only • Confidential Information
          </Typography>
        </Box>

        {/* Details Dialog */}
        <Dialog 
          open={isDetailsModalOpen} 
          onClose={closePropertyDetails}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1e293b' }}>
              <DescriptionIcon color="info" />
              <Typography variant="h6" fontWeight="600">Property Details</Typography>
            </Box>
            <IconButton onClick={closePropertyDetails} size="small"><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {detailsProperty && (
              <Stack spacing={3}>
                <Card variant="outlined" sx={{ bgcolor: '#eff6ff', borderColor: '#bfdbfe' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={8}>
                        <Typography variant="caption" color="text.secondary">TD Number</Typography>
                        <Typography variant="h4" fontWeight="800" color="#1e293b">{detailsProperty.td_number}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          PIN: {detailsProperty.pin || 'N/A'} {detailsProperty.arpn ? ` • ARPN: ${detailsProperty.arpn}` : ''}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4} sx={{ textAlign: { sm: 'right' } }}>
                        <Typography variant="caption" color="text.secondary">Owner</Typography>
                        <Typography variant="h6" fontWeight="600">{detailsProperty.owner_name || getOwnerName(detailsProperty.owner_id)}</Typography>
                        <Typography variant="subtitle2" color="primary">Barangay: {detailsProperty.barangay || 'N/A'}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Kind / Classification</Typography>
                        <Typography variant="body1" fontWeight="500">
                          {(propertyKinds.find(k => k.kind_id === detailsProperty.kind_id)?.kind_name) || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(propertyClassifications.find(c => c.classification_id === detailsProperty.classification_id)?.classification_name) || 'N/A'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="caption" color="text.secondary">Assessed Value</Typography>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                          {detailsProperty.assessed_value ? `₱${parseFloat(detailsProperty.assessed_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Effectivity Year: {detailsProperty.effectivity_year}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {remarksText && (
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary">Remarks</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{remarksText}</Typography>
                    </CardContent>
                  </Card>
                )}

                <Card variant="outlined">
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">Payors</Typography>
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {payorNames.filter(n => n && n.trim()).map((name, idx) => (
                            <Chip key={idx} label={name} size="small" />
                          ))}
                        </Box>
                      </Box>
                      <Button 
                        size="small" 
                        variant="outlined" 
                        startIcon={<PersonAddIcon />}
                        onClick={() => {
                          setPayorCount(payorCount + 1);
                          setPayorNames([...payorNames, '']);
                        }}
                      >
                        Add Payor
                      </Button>
                    </Box>

                    <Grid container spacing={2}>
                      {payorNames.map((name, index) => (
                        <Grid item xs={12} sm={6} key={index}>
                          <TextField
                            fullWidth
                            size="small"
                            label={`Payor ${index + 1} Name`}
                            value={name}
                            onChange={(e) => {
                              const newNames = [...payorNames];
                              newNames[index] = e.target.value;
                              setPayorNames(newNames);
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      const newNames = [...payorNames];
                                      newNames.splice(index, 1);
                                      setPayorNames(newNames.length ? newNames : ['']);
                                      setPayorCount(Math.max(1, payorCount - 1));
                                    }}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </InputAdornment>
                              )
                            }}
                          />
                        </Grid>
                      ))}
                      <Grid item xs={12}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                          <Button onClick={() => { setPayorCount(1); setPayorNames(['']); }}>Clear All</Button>
                          <Button 
                            variant="contained" 
                            onClick={async () => {
                              if (!detailsProperty) return;
                              const serialized = serializePayorsToRemarks(payorCount, payorNames, remarksText);
                              const result = await rptService.updateProperty(detailsProperty.property_id, { remarks: serialized });
                              if (result && result.success) {
                                const updated = { ...detailsProperty, remarks: serialized };
                                setDetailsProperty(updated);
                                await loadProperties();
                                const ownerName = detailsProperty.owner_name || getOwnerName(detailsProperty.owner_id);
                                showAlert(
                                  'Payors Saved',
                                  `TD Number: ${detailsProperty.td_number}\nOwner: ${ownerName}\nPayors Count: ${Math.max(1, parseInt(payorCount) || 1)}`,
                                  'success'
                                );
                              }
                            }}
                          >
                            Save Payors
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Stack>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={closePropertyDetails} sx={{ color: '#64748b' }}>Close</Button>
            <Button 
              variant="contained" 
              color="success"
              startIcon={<AttachMoneyIcon />}
              onClick={() => detailsProperty && handlePayProperty(detailsProperty)}
            >
              Pay
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Modal */}
        <Dialog 
          open={isAddModalOpen} 
          onClose={() => setIsAddModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#1e293b', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AddIcon /> Add New Property
            </Box>
            <IconButton onClick={() => setIsAddModalOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {renderPropertyForm()}
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <FormControlLabel
                control={<Checkbox checked={keepModalOpen} onChange={(e) => setKeepModalOpen(e.target.checked)} />}
                label="Keep dialog open for continuous entry"
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setIsAddModalOpen(false)}>Cancel</Button>
            <Button variant="contained" color="success" onClick={handleAddProperty}>
              Add Property
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Modal */}
        <Dialog 
          open={isEditModalOpen} 
          onClose={() => setIsEditModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#3b82f6', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <EditIcon /> Edit Property
            </Box>
            <IconButton onClick={() => setIsEditModalOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            {renderPropertyForm(true)}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setIsEditModalOpen(false)}>Cancel</Button>
            <Button variant="contained" color="info" onClick={handleEditProperty}>
              Save Changes
            </Button>
          </DialogActions>
        </Dialog>

        {/* Assessments Modal */}
        <Dialog 
          open={isAssessmentsModalOpen} 
          onClose={() => setIsAssessmentsModalOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: 2 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f59e0b', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HistoryIcon /> Assessed Value History - {selectedProperty?.td_number}
            </Box>
            <IconButton onClick={() => setIsAssessmentsModalOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Box sx={{ mb: 3 }}>
              <Button 
                variant="contained" 
                onClick={() => setIsAddAssessmentOpen(!isAddAssessmentOpen)}
                startIcon={isAddAssessmentOpen ? <CloseIcon /> : <AddIcon />}
                sx={{ bgcolor: isAddAssessmentOpen ? '#ef4444' : '#1e293b' }}
              >
                {isAddAssessmentOpen ? 'Cancel' : 'Add Assessment'}
              </Button>
            </Box>

            {/* Add Assessment Form */}
            <Collapse in={isAddAssessmentOpen}>
              <Card variant="outlined" sx={{ mb: 3, bgcolor: '#f0fdf4', borderColor: '#bbf7d0' }}>
                <CardContent>
                  <Typography variant="subtitle1" color="success.main" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon fontSize="small" /> New Assessment
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Assessed Value *"
                        type="number"
                        inputProps={{ step: "0.01" }}
                        value={assessmentForm.assessed_value}
                        onChange={(e) => setAssessmentForm({...assessmentForm, assessed_value: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Effectivity Year *"
                        type="number"
                        value={assessmentForm.effectivity_year}
                        onChange={(e) => setAssessmentForm({...assessmentForm, effectivity_year: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Revision Reason"
                        placeholder="e.g., General Revision, Improvement, Market Adjustment"
                        value={assessmentForm.revision_reason}
                        onChange={(e) => setAssessmentForm({...assessmentForm, revision_reason: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Remarks"
                        multiline
                        rows={2}
                        value={assessmentForm.remarks}
                        onChange={(e) => setAssessmentForm({...assessmentForm, remarks: e.target.value})}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button variant="contained" color="success" onClick={handleAddAssessment}>
                        Save Assessment
                      </Button>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Collapse>

            {/* Assessment History Table */}
            {loadingAssessments ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CircularProgress />
                <Typography sx={{ mt: 2 }} color="text.secondary">Loading assessment history...</Typography>
              </Box>
            ) : assessments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <HistoryIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                <Typography color="text.secondary">No assessment history</Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead sx={{ bgcolor: '#f8fafc' }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>Effectivity Year</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Assessed Value</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Revision Reason</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>Remarks</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {assessments.map((assessment, index) => (
                      <TableRow key={assessment.assessment_id} hover>
                        <TableCell>
                          <strong>{assessment.effectivity_year}</strong>
                          {index === 0 && <Chip label="Current" size="small" color="success" sx={{ ml: 1, height: 20, fontSize: '0.7rem' }} />}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          ₱{parseFloat(assessment.assessed_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>{assessment.revision_reason || 'N/A'}</TableCell>
                        <TableCell>{assessment.remarks || '-'}</TableCell>
                        <TableCell align="center">
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteAssessment(assessment)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', p: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 2 }}>
              <strong>{assessments.length}</strong> assessment{assessments.length !== 1 ? 's' : ''} recorded
            </Typography>
            <Button onClick={() => setIsAssessmentsModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Global Alert/Confirm Dialog */}
        <AlertDialog
          open={dialogConfig.open}
          onClose={closeDialog}
          title={dialogConfig.title}
          message={dialogConfig.message}
          type={dialogConfig.type === 'confirm' ? 'info' : dialogConfig.type}
          confirm={dialogConfig.type === 'confirm'}
          onConfirm={dialogConfig.onConfirm}
          autoCloseMs={dialogConfig.autoCloseMs}
          persistent={dialogConfig.persistent}
        />

        {/* Last Payment Year Modal */}
        <Dialog 
          open={isLastYearModalOpen}
          onClose={closeLastPaymentYearModal}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Set Last Payment Year</DialogTitle>
          <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {lastYearProperty ? `TD: ${lastYearProperty.td_number}` : ''}
            </Typography>
            <TextField
              label="Year"
              type="number"
              fullWidth
              inputRef={lastPaymentYearInputRef}
              value={lastPaymentYearInput}
              onChange={(e) => setLastPaymentYearInput(e.target.value)}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  saveLastPaymentYear();
                }
              }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              This records a year-only payment with amount ₱0 for status tracking.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeLastPaymentYearModal}>Cancel</Button>
            <Button variant="contained" onClick={saveLastPaymentYear}>Save</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Properties;
